'use client';

import { Stack, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';

import { HighLightHorizontal } from '@/assets/icons';
import { backgrounds, dividers } from '@/assets/materials';
import HexagonalButton from '@/components/bases/HexagonalButton';
import { useGA } from '@/hooks/useGA';
import { useGoogleCalendar } from '@/providers/GoogleCalendarProvider';

export const HeroSection = () => {
  const { trackButtonClickEvent } = useGA();
  const { showModal } = useGoogleCalendar();

  return (
    <Stack
      width={'100%'}
      // height={{ xs: '812px', sm: '720px' }}
      flexGrow={1}
      alignItems={'center'}
      justifyContent={'flex-start'}
      component={'section'}
      position={'relative'}
      sx={{
        backgroundImage: {
          xs: `url(${backgrounds.hero_background_mobile})`,
          sm: `url(${backgrounds.hero_background})`,
        },
        backgroundSize: { xs: '100% 100%', sm: 'cover' },
        objectFit: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}>
      <Stack
        zIndex={1}
        position={'absolute'}
        top={0}
        left={0}
        right={0}
        bottom={0}
        sx={{
          background:
            'linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), linear-gradient(180deg, rgba(6, 9, 18, 0) 50%, #060912 100%)',
        }}
      />
      <Stack
        maxWidth={{ sm: '1080px' }}
        width={'100%'}
        zIndex={2}
        alignItems={'center'}
        gap={{ xs: 10, sm: 14 }}
        paddingTop={{ xs: '120.5px', sm: '161px' }}
        paddingBottom={{ xs: '120.5px', sm: '81px' }}
        paddingX='16px'
        marginX={'auto'}>
        {/* Top Divider */}
        <Stack width={'100%'} alignItems={'center'} direction={'row'} gap={2}>
          <HighLightHorizontal width={'65px'} height={'10px'} color={'#0ACBE6'} />
          <Stack className={'w-full'} justifyContent={'center'}>
            <Image
              src={dividers.divider_2}
              alt={'divider'}
              width={730}
              height={2}
              style={{ width: '100%', height: 'auto' }}
            />
          </Stack>
        </Stack>

        {/* Text Container - Desktop: 56px gap, Mobile: 16px gap */}
        <Stack sx={{ gap: { xs: '16px', sm: '56px' } }} width={'100%'} alignItems={'center'}>
          {/* Title */}
          <Typography
            component={'h1'}
            variant={'displayMedium'}
            className={'clip-text'}
            fontSize={{ xs: '32px', md: '38px', lg: '48px' }}
            lineHeight={{ xs: '40px', md: '38px', lg: '68px' }}
            fontWeight={700}
            textAlign={'center'}
            sx={{
              background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)',
            }}>
            GLOBAL HIRING, PAYROLL & COMPLIANCE
            <br />
            ONE PLATFORM. GLOBAL TEAMS.
          </Typography>

          {/* Tags - Desktop: row with 32px gap, Mobile: column with 8px gap */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={'center'}
            justifyContent={'center'}
            paddingX={{ xs: 0, sm: '80px' }}
            width={'100%'}
            sx={{ gap: { xs: '8px', sm: '32px' } }}>
            <Typography
              fontSize={{ xs: '14px', sm: '18px' }}
              lineHeight={{ xs: '20px', sm: '24px' }}
              variant={'bodyLarge'}
              color={'#FFFFFF'}
              textAlign={'center'}
              sx={{ opacity: 0.65, height: '48px', display: 'flex', alignItems: 'center' }}>
              Hire employees and contractors worldwide.
            </Typography>
            <Stack
              width={{ xs: '100%', sm: '1px' }}
              height={{ xs: '1px', sm: '24px' }}
              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
            />
            <Typography
              fontSize={{ xs: '14px', sm: '18px' }}
              lineHeight={{ xs: '20px', sm: '24px' }}
              variant={'bodyLarge'}
              color={'#FFFFFF'}
              textAlign={'center'}
              sx={{ opacity: 0.65, height: '48px', display: 'flex', alignItems: 'center' }}>
              Stay compliant without setting up entities.
            </Typography>
            <Stack
              width={{ xs: '100%', sm: '1px' }}
              height={{ xs: '1px', sm: '24px' }}
              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
            />
            <Typography
              fontSize={{ xs: '14px', sm: '18px' }}
              lineHeight={{ xs: '20px', sm: '24px' }}
              variant={'bodyLarge'}
              color={'#FFFFFF'}
              textAlign={'center'}
              sx={{ opacity: 0.65, height: '48px', display: 'flex', alignItems: 'center' }}>
              Pay teams in fiat or digital assets.
            </Typography>
          </Stack>
        </Stack>

        {/* Button - Desktop: 191px x 52px, Mobile: 140px x 40px */}
        <Stack>
          {/* Desktop Button */}
          <Stack display={{ xs: 'none', sm: 'flex' }}>
            <HexagonalButton
              variant={'secondary'}
              label={'CONTACT US'}
              width={191}
              height={52}
              onClick={() => {
                trackButtonClickEvent({ name: 'contact_us' });
                showModal();
              }}
            />
          </Stack>
          {/* Mobile Button */}
          <Stack display={{ xs: 'flex', sm: 'none' }}>
            <HexagonalButton
              variant='primary'
              label='CONTACT US'
              width={140}
              labelClassName='!text-[24px]'
              zoom={0.8}
              onClick={() => {
                trackButtonClickEvent({ name: 'contact_us' });
                showModal();
              }}
            />
          </Stack>
        </Stack>

        {/* Bottom Divider */}
        <Stack width={'100%'} alignItems={'center'} direction={'row'} gap={2}>
          <Stack className={'w-full'} justifyContent={'center'}>
            <Image
              src={dividers.divider_2}
              alt={'divider'}
              width={730}
              height={2}
              style={{ width: '100%', height: 'auto', transform: 'rotate(180deg)' }}
            />
          </Stack>
          <HighLightHorizontal width={'65px'} height={'10px'} color={'#FFA719'} />
        </Stack>
      </Stack>
    </Stack>
  );
};
